/**
 * The checker's document-changing fixes: the `problems.fix.*` commands (W2-12a checker-runtime; DESIGN.md §5.10,
 * §7.6; ARCHITECTURE.md §6.1: "Document-changing checker fixes are W2-12a's own problems.fix.* commands that call
 * Core bodies directly").
 *
 * Every id the rules reference is here: the 30 ids of core/check/geometry.ts `FIX_COMMANDS` (argument shapes
 * `FixArgs`, W2-12b) and the 5 of core/check/style.ts `STYLE_FIX_COMMANDS`. Each fix
 *  1. validates its JSON arguments (a problem found on an older revision may name records that are gone: those are
 *     skipped, and a fix with nothing left to do fails with "the chart changed");
 *  2. runs Core command bodies (or, where Core has no body, a small body of its own on the TxBuilder) in ONE
 *     transaction, labelled with the fix's own label, so it is a single undo step and appears in History;
 *  3. media fixes (convert to WAV / PNG, re-time GIF frames, downscale) first produce and store the new file
 *     (media.ts), then add it, re-point every reference, drop the old file when nothing uses it any more and give
 *     the new file the old name where it fits, all in that one transaction.
 *
 * A fix is an invocation of a registered command (FixRef), so any package may run one through
 * `registry.runAny(fix.command, fix.args)` (Problems panel and Ctrl+. go through `services.problems.applyFix`, which
 * also names the history entry after the fix; Publish's check summary may do either). The commands carry the
 * condition `!paletteOpen`: without a problem's arguments they mean nothing, so the palette lists them only when
 * searched, among the commands that do not apply. `runFix` is the same logic without the registry (problems
 * service fallback, tests). Media failures are shown with a message per failure kind (media.ts `MediaFixError`);
 * the technical text goes to the console.
 */

import type { CommandContext, CommandDef, IconName, UiService } from '../../app/registry/types';
import { FIX_COMMANDS, type FixCommandId } from '../../core/check/geometry';
import {
  STYLE_FIX_COMMANDS,
  keysOfTrack,
  linearRuns,
  redundantLinearKeys,
  type StyleFixCommandId,
} from '../../core/check/style';
import { assetReferenceCount, sanitiseAssetPath } from '../../core/commands/assets';
import { Core } from '../../core/commands/index';
import { HOLD } from '../../core/ease/index';
import type { BezierPoints, Ease } from '../../core/ease/types';
import type {
  AssetId,
  AssetRef,
  EffectId,
  EffectVarType,
  Key,
  KeyId,
  LineId,
  LineProp,
  LineRpeData,
  NoteId,
  NoteKind,
  TrackId,
  TrackValue,
} from '../../core/model/types';
import { isLineProp } from '../../core/model/props';
import type { TxBuilder, Txn } from '../../core/ops/types';
import { Beats } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import type { Json, MessageRef, RGBA8, Vec2 } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import type { DocStore } from '../../state/types';
import { m } from './checker.messages';
import {
  MediaFixError,
  convertAudioToWav,
  convertImageToPng,
  downscaleImage,
  reencodeGif,
  type MediaFailure,
  type MediaServices,
} from './media';

// ---------------------------------------------------------------------------------------------------------------
// Ids
// ---------------------------------------------------------------------------------------------------------------

export type AnyFixCommandId = FixCommandId | StyleFixCommandId;

/** Every `problems.fix.*` command (rule fixes and style fixes). */
export const FIX_COMMAND_IDS: readonly AnyFixCommandId[] = Object.freeze([...FIX_COMMANDS, ...STYLE_FIX_COMMANDS]);

const FIX_ID_SET: ReadonlySet<string> = new Set(FIX_COMMAND_IDS);

export function isFixCommand(id: string): id is AnyFixCommandId {
  return FIX_ID_SET.has(id);
}

/** Module when-atom that holds while the problems service applies a fix (for packages that want to know). */
export const FIXING_ATOM = 'problems.fixing';

/** Condition of the fix commands: everywhere except in the palette's default list (they need a problem's args). */
export const FIX_COMMAND_WHEN = '!paletteOpen';

const TITLES: Readonly<Record<AnyFixCommandId, MessageKey>> = {
  'problems.fix.setLineOpacityFull': m.fixSetLineOpacityFull,
  'problems.fix.holdLeadIn': m.fixHoldLeadIn,
  'problems.fix.setEase': m.fixSetEase,
  'problems.fix.setKeyValue': m.fixSetKeyValue,
  'problems.fix.clampKeyValues': m.fixClampKeyValues,
  'problems.fix.removeGifProgress': m.fixRemoveGifProgress,
  'problems.fix.clampPreviewRange': m.fixClampPreviewRange,
  'problems.fix.snapNotesX': m.fixSnapNotesX,
  'problems.fix.deleteNotes': m.fixDeleteNotes,
  'problems.fix.moveNotesLater': m.fixMoveNotesLater,
  'problems.fix.alignNoteTimes': m.fixAlignNoteTimes,
  'problems.fix.setIncline': m.fixSetIncline,
  'problems.fix.resetRpeLineFields': m.fixResetRpeLineFields,
  'problems.fix.addWhiteColorKey': m.fixAddWhiteColorKey,
  'problems.fix.holdVarLeadIn': m.fixHoldVarLeadIn,
  'problems.fix.renameAsset': m.fixRenameAsset,
  'problems.fix.renameHitsoundAsset': m.fixRenameHitsoundAsset,
  'problems.fix.deleteEffect': m.fixDeleteEffect,
  'problems.fix.setEffectVar': m.fixSetEffectVar,
  'problems.fix.writeIntegerRpeVersion': m.fixWriteIntegerRpeVersion,
  'problems.fix.extendHolds': m.fixExtendHolds,
  'problems.fix.convertNotes': m.fixConvertNotes,
  'problems.fix.setControl': m.fixSetControl,
  'problems.fix.removeAssetReferences': m.fixRemoveAssetReferences,
  'problems.fix.convertAudioToWav': m.fixConvertAudioToWav,
  'problems.fix.convertImageToPng': m.fixConvertImageToPng,
  'problems.fix.reencodeGif': m.fixReencodeGif,
  'problems.fix.downscaleImage': m.fixDownscaleImage,
  'problems.fix.moveNotesToNewLine': m.fixMoveNotesToNewLine,
  'problems.fix.removeIgnoredLineData': m.fixRemoveIgnoredLineData,
  'problems.fix.confirmTiming': m.fixConfirmTiming,
  'problems.fix.removeAssets': m.fixRemoveAssets,
  'problems.fix.deleteLines': m.fixDeleteLines,
  'problems.fix.removeRedundantKeys': m.fixRemoveRedundantKeys,
  'problems.fix.addTag': m.fixAddTag,
};

const ICONS: Partial<Readonly<Record<AnyFixCommandId, IconName>>> = {
  'problems.fix.deleteNotes': 'Trash2',
  'problems.fix.deleteEffect': 'Trash2',
  'problems.fix.deleteLines': 'Trash2',
  'problems.fix.removeAssets': 'Trash2',
  'problems.fix.removeAssetReferences': 'Trash2',
  'problems.fix.convertAudioToWav': 'FileAudio',
  'problems.fix.convertImageToPng': 'FileImage',
  'problems.fix.reencodeGif': 'FileImage',
  'problems.fix.downscaleImage': 'Shrink',
  'problems.fix.renameAsset': 'FilePen',
  'problems.fix.renameHitsoundAsset': 'FilePen',
};

// ---------------------------------------------------------------------------------------------------------------
// Environment and outcome
// ---------------------------------------------------------------------------------------------------------------

export interface FixEnv {
  readonly store: DocStore | null;
  /** Beat snap divisor: "one snap step" fixes move or lengthen by 1/snapDiv beat. */
  readonly snapDiv: number;
  /** X grid line count for "snap X". */
  readonly xGrid: number;
  /** History label (the fix's own label when it runs from a problem); null = the command title. */
  readonly label: MessageRef | null;
  /** Storage and workers for the media fixes (null: those fixes are unavailable). */
  readonly media: MediaServices | null;
}

export type FixOutcome =
  | { readonly ok: true; readonly txn: Txn | null; readonly notes: readonly MessageRef[] }
  | { readonly ok: false; readonly reason: MessageRef };

function ref(key: MessageKey, params?: Readonly<Record<string, string | number>>): MessageRef {
  return params === undefined ? { key } : { key, params };
}

// ---------------------------------------------------------------------------------------------------------------
// Argument parsing
// ---------------------------------------------------------------------------------------------------------------

class ArgError extends Error {}

type Args = Readonly<Record<string, Json>>;

function argsObject(a: unknown): Args {
  if (a === null || a === undefined || typeof a !== 'object' || Array.isArray(a)) throw new ArgError('arguments');
  return a as Args;
}

function num(a: Args, k: string): number {
  const v = a[k];
  if (typeof v !== 'number' || !Number.isFinite(v)) throw new ArgError(k);
  return v;
}

function int(a: Args, k: string): number {
  const v = num(a, k);
  if (!Number.isSafeInteger(v)) throw new ArgError(k);
  return v;
}

function str(a: Args, k: string): string {
  const v = a[k];
  if (typeof v !== 'string' || v.length === 0) throw new ArgError(k);
  return v;
}

function list(a: Args, k: string): readonly Json[] {
  const v = a[k];
  if (!Array.isArray(v)) throw new ArgError(k);
  return v as readonly Json[];
}

function ints(a: Args, k: string): number[] {
  return list(a, k).map((v) => {
    if (typeof v !== 'number' || !Number.isSafeInteger(v)) throw new ArgError(k);
    return v;
  });
}

function strs(a: Args, k: string): string[] {
  return list(a, k).map((v) => {
    if (typeof v !== 'string' || v.length === 0) throw new ArgError(k);
    return v;
  });
}

function oneOf<T extends string>(a: Args, k: string, values: readonly T[]): T {
  const v = a[k];
  const hit = values.find((x) => x === v);
  if (hit === undefined) throw new ArgError(k);
  return hit;
}

function numOrNull(a: Args, k: string): number | null {
  const v = a[k];
  if (v === null) return null;
  return num(a, k);
}

function finiteList(v: Json, k: string, len?: number): number[] {
  if (!Array.isArray(v)) throw new ArgError(k);
  const out = (v as readonly Json[]).map((x) => {
    if (typeof x !== 'number' || !Number.isFinite(x)) throw new ArgError(k);
    return x;
  });
  if (len !== undefined && out.length !== len) throw new ArgError(k);
  return out;
}

function beatOf(a: Args, k: string): Beat {
  const v = a[k];
  if (v === null || typeof v !== 'object' || Array.isArray(v)) throw new ArgError(k);
  const o = v as Args;
  try {
    return Beats.make(int(o, 'w'), int(o, 'n'), int(o, 'd'));
  } catch {
    throw new ArgError(k);
  }
}

function easeOf(a: Args, k: string): Ease {
  const v = a[k];
  if (v === null || typeof v !== 'object' || Array.isArray(v)) throw new ArgError(k);
  const o = v as Args;
  switch (o.kind) {
    case 'hold':
      return HOLD;
    case 'tween':
      return { kind: 'tween', id: int(o, 'id'), left: num(o, 'left'), right: num(o, 'right') };
    case 'bezier': {
      const p = finiteList(o.p ?? null, k, 4);
      return { kind: 'bezier', p: [p[0], p[1], p[2], p[3]] as unknown as BezierPoints };
    }
    default:
      throw new ArgError(k);
  }
}

/** A track value: number, string or a list of numbers (RGB). */
function valueOf(a: Args, k: string): TrackValue {
  const v = a[k];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') return v;
  const l = finiteList(v ?? null, k);
  if (l.length === 2) return [l[0], l[1]] as unknown as Vec2;
  if (l.length === 3) return [l[0], l[1], l[2]] as unknown as TrackValue;
  if (l.length === 4) return [l[0], l[1], l[2], l[3]] as unknown as RGBA8;
  throw new ArgError(k);
}

// ---------------------------------------------------------------------------------------------------------------
// Shared bodies
// ---------------------------------------------------------------------------------------------------------------

function gone(tx: TxBuilder): never {
  return tx.fail(ref(m.fixGone));
}

function existingNotes(tx: TxBuilder, ids: readonly number[]): NoteId[] {
  const out = ids.filter((id) => tx.doc.notes.has(id as NoteId)) as NoteId[];
  if (out.length === 0) gone(tx);
  return out;
}

function existingKeys(tx: TxBuilder, ids: readonly number[]): KeyId[] {
  const out = ids.filter((id) => tx.doc.keys.has(id as KeyId)) as KeyId[];
  if (out.length === 0) gone(tx);
  return out;
}

function requireLine(tx: TxBuilder, id: number): LineId {
  if (!tx.doc.lines.has(id as LineId)) gone(tx);
  return id as LineId;
}

function requireTrack(tx: TxBuilder, id: number): TrackId {
  if (!tx.doc.tracks.has(id as TrackId)) gone(tx);
  return id as TrackId;
}

function requireAsset(tx: TxBuilder, id: string): AssetId {
  if (!tx.doc.assets.has(id as AssetId)) gone(tx);
  return id as AssetId;
}

function snapStep(env: FixEnv): Beat {
  const div = Number.isSafeInteger(env.snapDiv) && env.snapDiv > 0 ? env.snapDiv : 4;
  return Beats.make(0, 1, div);
}

/** Every track of a line property (all layers). */
function propTracks(tx: TxBuilder, line: LineId, prop: LineProp): TrackId[] {
  return [...(tx.idx.tracksOfLine(line).get(prop) ?? [])];
}

function layerOfTrack(tx: TxBuilder, track: TrackId): number {
  const t = tx.doc.tracks.get(track);
  return t !== undefined && t.owner.kind === 'line' ? t.owner.layer : 0;
}

/** Deletes every layer of a line property. */
function resetAllLayers(tx: TxBuilder, line: LineId, prop: LineProp): void {
  for (const track of propTracks(tx, line, prop)) {
    if (!tx.doc.tracks.has(track)) continue;
    Core.keys.resetProperty(tx, { line, prop, layer: layerOfTrack(tx, track) });
  }
}

/**
 * Adds `ref`, re-points every reference of `from` to it, removes `from` when nothing uses it any more and gives the
 * new file the old file's (sanitised) name when that name is free.
 */
function replaceWithNew(tx: TxBuilder, from: AssetId, ref: AssetRef, desiredPath: string): void {
  Core.doc.addAsset(tx, { ref });
  if (from === ref.id) return;
  Core.doc.replaceAsset(tx, { from, to: ref.id });
  if (tx.doc.assets.has(from) && assetReferenceCount(tx, from) === 0) {
    Core.doc.removeAsset(tx, { id: from, references: 'fail' });
  }
  const added = tx.doc.assets.get(ref.id);
  if (added === undefined) return;
  const path = sanitiseAssetPath(desiredPath, added.role);
  if (added.path === path) return;
  for (const a of tx.doc.assets.values()) if (a.id !== added.id && a.path === path) return;
  Core.doc.renameAsset(tx, { id: added.id, path });
}

// ---------------------------------------------------------------------------------------------------------------
// Fix specifications
// ---------------------------------------------------------------------------------------------------------------

interface SyncFix<P> {
  readonly kind: 'sync';
  parse(a: Args): P;
  apply(tx: TxBuilder, p: P, env: FixEnv): void;
}

interface MediaFix<P> {
  readonly kind: 'media';
  parse(a: Args): P;
  /** The asset the fix replaces. */
  source(p: P): AssetId;
  produce(p: P, from: AssetRef, media: MediaServices): Promise<AssetRef>;
}

type FixSpec = SyncFix<unknown> | MediaFix<unknown>;

function sync<P>(s: Omit<SyncFix<P>, 'kind'>): FixSpec {
  return { kind: 'sync', ...s };
}

function media<P>(s: Omit<MediaFix<P>, 'kind'>): FixSpec {
  return { kind: 'media', ...s };
}

const NOTE_KINDS: readonly NoteKind[] = ['tap', 'drag', 'flick', 'hold'];
const VAR_TYPES: readonly EffectVarType[] = ['float', 'vec2', 'color'];
const CONTROL_WHICH = ['pos', 'alpha', 'size', 'y'] as const;
const RPE_LINE_FIELDS = ['bpmfactor', 'anchor', 'skewControl'] as const;

const SPECS: Readonly<Record<AnyFixCommandId, FixSpec>> = {
  'problems.fix.setLineOpacityFull': sync({
    parse: (a) => ({ line: int(a, 'line') }),
    apply(tx, p) {
      const line = requireLine(tx, p.line);
      Core.keys.ensureTrack(tx, { owner: { kind: 'line', line, prop: 'alpha', layer: 0 }, value: 255, at: Beats.ZERO });
    },
  }),

  'problems.fix.holdLeadIn': sync({
    parse: (a) => ({ track: int(a, 'track') }),
    apply(tx, p) {
      Core.keys.holdLeadIn(tx, { track: requireTrack(tx, p.track) });
    },
  }),

  'problems.fix.setEase': sync({
    parse: (a) => ({ segments: ints(a, 'segments'), ease: easeOf(a, 'ease') }),
    apply(tx, p) {
      Core.keys.setEase(tx, { segments: existingKeys(tx, p.segments), ease: p.ease });
    },
  }),

  'problems.fix.setKeyValue': sync({
    parse: (a) => ({
      key: int(a, 'key'),
      part: oneOf(a, 'part', ['in', 'out', 'both'] as const),
      value: valueOf(a, 'value'),
    }),
    apply(tx, p) {
      Core.keys.setKeyValues(tx, { keys: existingKeys(tx, [p.key]), part: p.part, value: p.value });
    },
  }),

  'problems.fix.clampKeyValues': sync({
    parse: (a) => {
      const min = num(a, 'min');
      const max = num(a, 'max');
      if (!(min <= max)) throw new ArgError('min');
      return { keys: ints(a, 'keys'), min, max };
    },
    apply(tx, p) {
      const clamp = (v: number): number => Math.min(p.max, Math.max(p.min, v));
      const keys = existingKeys(tx, p.keys)
        .map((id) => tx.doc.keys.get(id))
        .filter((k): k is Key => k !== undefined)
        .sort((a, b) => (a.track !== b.track ? a.track - b.track : Beats.cmp(a.t, b.t)));
      for (const k0 of keys) {
        // Read again: clamping a hold key's outgoing value also sets the next key's arriving value.
        const k = tx.doc.keys.get(k0.id);
        if (k === undefined || typeof k.vIn !== 'number' || typeof k.vOut !== 'number') continue;
        const cOut = clamp(k.vOut);
        if (k.vIn === k.vOut) {
          if (cOut !== k.vOut) Core.keys.setKeyValues(tx, { keys: [k.id], part: 'both', value: cOut });
          continue;
        }
        if (cOut !== k.vOut) Core.keys.setKeyValues(tx, { keys: [k.id], part: 'out', value: cOut });
        const after = tx.doc.keys.get(k.id);
        if (after !== undefined && typeof after.vIn === 'number' && clamp(after.vIn) !== after.vIn) {
          Core.keys.setKeyValues(tx, { keys: [k.id], part: 'in', value: clamp(after.vIn) });
        }
      }
    },
  }),

  'problems.fix.removeGifProgress': sync({
    parse: (a) => ({ lines: ints(a, 'lines') }),
    apply(tx, p) {
      const lines = p.lines.filter((l) => tx.doc.lines.has(l as LineId)) as LineId[];
      if (lines.length === 0) gone(tx);
      for (const line of lines) {
        resetAllLayers(tx, line, 'gif');
        if (tx.doc.lines.get(line)?.gifAutoLoop === true) Core.lines.setGifAutoLoop(tx, { line, on: false });
      }
    },
  }),

  'problems.fix.clampPreviewRange': sync({
    parse: (a) => ({ start: num(a, 'start'), end: numOrNull(a, 'end') }),
    apply(tx, p) {
      Core.doc.patchMeta(tx, { patch: { previewStart: p.start, previewEnd: p.end } });
    },
  }),

  'problems.fix.snapNotesX': sync({
    parse: (a) => ({ notes: ints(a, 'notes') }),
    apply(tx, p, env) {
      const count = Number.isSafeInteger(env.xGrid) && env.xGrid >= 2 ? env.xGrid : 11;
      Core.notes.snapNotesX(tx, { ids: existingNotes(tx, p.notes), count });
    },
  }),

  'problems.fix.deleteNotes': sync({
    parse: (a) => ({ notes: ints(a, 'notes') }),
    apply(tx, p) {
      Core.notes.deleteNotes(tx, { ids: existingNotes(tx, p.notes) });
    },
  }),

  'problems.fix.moveNotesLater': sync({
    parse: (a) => ({ notes: ints(a, 'notes') }),
    apply(tx, p, env) {
      Core.notes.moveNotes(tx, { ids: existingNotes(tx, p.notes), dt: snapStep(env), dx: 0 });
    },
  }),

  'problems.fix.alignNoteTimes': sync({
    parse: (a) => ({ notes: ints(a, 'notes'), beat: beatOf(a, 'beat') }),
    apply(tx, p) {
      for (const id of existingNotes(tx, p.notes)) {
        const n = tx.doc.notes.get(id);
        if (n === undefined) continue;
        if (n.kind === 'hold' && Beats.lt(n.t, n.end)) {
          Core.notes.setNoteTimes(tx, { id, t: p.beat, end: Beats.add(p.beat, Beats.sub(n.end, n.t)) });
        } else {
          Core.notes.setNoteTimes(tx, { id, t: p.beat });
        }
      }
    },
  }),

  'problems.fix.setIncline': sync({
    parse: (a) => ({ line: int(a, 'line'), degrees: num(a, 'degrees') }),
    apply(tx, p) {
      Core.lines.setIncline(tx, { line: requireLine(tx, p.line), degrees: p.degrees });
    },
  }),

  'problems.fix.resetRpeLineFields': sync({
    parse: (a) => ({
      line: int(a, 'line'),
      fields: strs(a, 'fields').map((f) => {
        const hit = RPE_LINE_FIELDS.find((x) => x === f);
        if (hit === undefined) throw new ArgError('fields');
        return hit;
      }),
    }),
    apply(tx, p) {
      const id = requireLine(tx, p.line);
      const line = tx.doc.lines.get(id);
      if (line === undefined) gone(tx);
      const rpe: LineRpeData = {
        ...line.rpe,
        ...(p.fields.includes('bpmfactor') ? { bpmfactor: 1 } : {}),
        ...(p.fields.includes('anchor') ? { anchor: [0.5, 0.5] as const } : {}),
        ...(p.fields.includes('skewControl') ? { skewControl: null } : {}),
      };
      tx.update('lines', id, { rpe });
    },
  }),

  'problems.fix.addWhiteColorKey': sync({
    parse: (a) => ({ line: int(a, 'line') }),
    apply(tx, p) {
      const line = requireLine(tx, p.line);
      Core.keys.ensureTrack(tx, {
        owner: { kind: 'line', line, prop: 'color', layer: 0 },
        value: [255, 255, 255],
        at: Beats.ZERO,
      });
    },
  }),

  'problems.fix.holdVarLeadIn': sync({
    parse: (a) => ({ track: int(a, 'track'), beat: beatOf(a, 'beat') }),
    apply(tx, p) {
      const track = requireTrack(tx, p.track);
      const first = keysOfTrack(tx.doc, tx.idx, track)[0];
      if (first === undefined) gone(tx);
      if (!Beats.lt(p.beat, first.t)) return;
      const { key } = Core.keys.insertKey(tx, { track, t: p.beat, value: first.vIn });
      Core.keys.setEase(tx, { segments: [key], ease: HOLD });
    },
  }),

  'problems.fix.renameAsset': sync({
    parse: (a) => ({ asset: str(a, 'asset'), path: str(a, 'path') }),
    apply(tx, p) {
      Core.doc.renameAsset(tx, { id: requireAsset(tx, p.asset), path: p.path });
    },
  }),

  'problems.fix.renameHitsoundAsset': sync({
    parse: (a) => ({ asset: str(a, 'asset'), path: str(a, 'path'), notes: ints(a, 'notes') }),
    apply(tx, p) {
      const asset = requireAsset(tx, p.asset);
      Core.doc.renameAsset(tx, { id: asset, path: p.path });
      const notes = p.notes.filter((id) => tx.doc.notes.has(id as NoteId)) as NoteId[];
      if (notes.length > 0) Core.notes.setHitsound(tx, { ids: notes, hitsound: { kind: 'asset', asset } });
    },
  }),

  'problems.fix.deleteEffect': sync({
    parse: (a) => ({ effect: int(a, 'effect') }),
    apply(tx, p) {
      if (!tx.doc.effects.has(p.effect as EffectId)) gone(tx);
      Core.doc.deleteEffect(tx, { id: p.effect as EffectId });
    },
  }),

  'problems.fix.setEffectVar': sync({
    parse: (a) => {
      const type = oneOf(a, 'type', VAR_TYPES);
      const raw = a.value;
      let v: number | Vec2 | RGBA8;
      if (type === 'float') {
        v = num(a, 'value');
      } else if (type === 'vec2') {
        const l = finiteList(raw ?? null, 'value', 2);
        v = [l[0], l[1]] as unknown as Vec2;
      } else {
        const l = finiteList(raw ?? null, 'value', 4);
        v = [l[0], l[1], l[2], l[3]] as unknown as RGBA8;
      }
      return { effect: int(a, 'effect'), name: str(a, 'name'), type, value: v };
    },
    apply(tx, p) {
      if (!tx.doc.effects.has(p.effect as EffectId)) gone(tx);
      Core.doc.setEffectVar(tx, {
        id: p.effect as EffectId,
        v: { name: p.name, type: p.type, value: { kind: 'const', v: p.value } },
      });
    },
  }),

  'problems.fix.writeIntegerRpeVersion': sync({
    parse: (a) => ({ version: int(a, 'version') }),
    apply(tx, p) {
      Core.doc.patchPkg(tx, { patch: { rpeVersion: p.version, rpeVersionRaw: null } });
    },
  }),

  'problems.fix.extendHolds': sync({
    parse: (a) => ({ notes: ints(a, 'notes') }),
    apply(tx, p, env) {
      const step = snapStep(env);
      for (const id of existingNotes(tx, p.notes)) {
        const n = tx.doc.notes.get(id);
        if (n?.kind !== 'hold') continue;
        if (Beats.lt(n.t, n.end)) continue;
        Core.notes.setNoteTimes(tx, { id, end: Beats.add(n.t, step) });
      }
    },
  }),

  'problems.fix.convertNotes': sync({
    parse: (a) => ({ notes: ints(a, 'notes'), kind: oneOf(a, 'kind', NOTE_KINDS) }),
    apply(tx, p, env) {
      Core.notes.convertNotes(tx, { ids: existingNotes(tx, p.notes), kind: p.kind, holdLen: snapStep(env) });
    },
  }),

  'problems.fix.setControl': sync({
    parse: (a) => {
      const which = oneOf(a, 'which', CONTROL_WHICH);
      const curve = a.curve;
      if (curve === null || typeof curve !== 'object' || Array.isArray(curve)) throw new ArgError('curve');
      const points = list(curve as Args, 'points').map((pt) => {
        if (pt === null || typeof pt !== 'object' || Array.isArray(pt)) throw new ArgError('points');
        const o = pt as Args;
        const extraRaw = o.extra;
        const extra =
          extraRaw === undefined
            ? undefined
            : (list(o, 'extra').map((e) => {
                if (!Array.isArray(e) || e.length !== 2) throw new ArgError('extra');
                const [k, v] = e as readonly Json[];
                if (typeof k !== 'string' || typeof v !== 'number' || !Number.isFinite(v)) throw new ArgError('extra');
                return [k, v] as const;
              }) as ReadonlyArray<readonly [string, number]>);
        const base = { x: num(o, 'x'), easing: int(o, 'easing'), value: num(o, 'value') };
        return extra === undefined ? base : { ...base, extra };
      });
      return { line: int(a, 'line'), which, points };
    },
    apply(tx, p) {
      Core.lines.setControl(tx, { line: requireLine(tx, p.line), which: p.which, curve: { points: p.points } });
    },
  }),

  'problems.fix.removeAssetReferences': sync({
    parse: (a) => ({ asset: str(a, 'asset') }),
    apply(tx, p) {
      Core.doc.removeAsset(tx, { id: requireAsset(tx, p.asset), references: 'remove' });
    },
  }),

  'problems.fix.convertAudioToWav': media({
    parse: (a) => ({ asset: str(a, 'asset') as AssetId }),
    source: (p) => p.asset,
    produce: (_p, from, s) => convertAudioToWav(s, from),
  }),

  'problems.fix.convertImageToPng': media({
    parse: (a) => ({ asset: str(a, 'asset') as AssetId }),
    source: (p) => p.asset,
    produce: (_p, from, s) => convertImageToPng(s, from),
  }),

  'problems.fix.reencodeGif': media({
    parse: (a) => {
      const frameMs = num(a, 'frameMs');
      if (!(frameMs > 0)) throw new ArgError('frameMs');
      return { asset: str(a, 'asset') as AssetId, frameMs };
    },
    source: (p) => p.asset,
    produce: (p, from, s) => reencodeGif(s, from, p.frameMs),
  }),

  'problems.fix.downscaleImage': media({
    parse: (a) => {
      const maxSide = int(a, 'maxSide');
      if (maxSide < 1) throw new ArgError('maxSide');
      return { asset: str(a, 'asset') as AssetId, maxSide };
    },
    source: (p) => p.asset,
    produce: (p, from, s) => downscaleImage(s, from, p.maxSide),
  }),

  'problems.fix.moveNotesToNewLine': sync({
    parse: (a) => ({ line: int(a, 'line'), notes: ints(a, 'notes') }),
    apply(tx, p) {
      const line = requireLine(tx, p.line);
      const notes = existingNotes(tx, p.notes);
      const copy = Core.lines.duplicateLine(tx, { id: line, withNotes: false, withChildren: false, asChild: false });
      if (tx.doc.lines.get(copy)?.attachUI !== null) Core.lines.setAttachUI(tx, { line: copy, element: null });
      // Only the notes show: the copy's opacity is a constant 0 (its notes keep their own opacity).
      resetAllLayers(tx, copy, 'alpha');
      Core.keys.ensureTrack(tx, {
        owner: { kind: 'line', line: copy, prop: 'alpha', layer: 0 },
        value: 0,
        at: Beats.ZERO,
      });
      Core.notes.moveNotesToLine(tx, { ids: notes, line: copy });
      tx.setSelection({ notes, lines: [copy], activeLine: copy });
    },
  }),

  'problems.fix.removeIgnoredLineData': sync({
    parse: (a) => ({
      line: int(a, 'line'),
      props: strs(a, 'props').map((x) => {
        if (!isLineProp(x)) throw new ArgError('props');
        return x;
      }),
      keys: strs(a, 'keys'),
    }),
    apply(tx, p) {
      const id = requireLine(tx, p.line);
      for (const prop of p.props) resetAllLayers(tx, id, prop);
      const line = tx.doc.lines.get(id);
      if (line === undefined) return;
      const drop = new Set(p.keys);
      const ignored = line.rpe.ignored.filter((d) => !(d.writeBack === 'extended' && drop.has(d.key)));
      if (ignored.length !== line.rpe.ignored.length) tx.update('lines', id, { rpe: { ...line.rpe, ignored } });
    },
  }),

  // ---- style fixes ----

  'problems.fix.confirmTiming': sync({
    parse: () => ({}),
    apply(tx) {
      Core.tempo.setTimingConfirmed(tx, { confirmed: true });
    },
  }),

  'problems.fix.removeAssets': sync({
    parse: (a) => ({ assets: strs(a, 'assets') }),
    apply(tx, p) {
      let removed = 0;
      for (const raw of p.assets) {
        const id = raw as AssetId;
        if (!tx.doc.assets.has(id) || assetReferenceCount(tx, id) > 0) continue;
        Core.doc.removeAsset(tx, { id, references: 'fail' });
        removed++;
      }
      if (removed === 0) gone(tx);
    },
  }),

  'problems.fix.deleteLines': sync({
    parse: (a) => ({ lines: ints(a, 'lines') }),
    apply(tx, p) {
      const lines = p.lines.filter((l) => tx.doc.lines.has(l as LineId)) as LineId[];
      if (lines.length === 0) gone(tx);
      Core.lines.deleteLines(tx, { ids: lines, children: 'detach' });
    },
  }),

  'problems.fix.removeRedundantKeys': sync({
    parse: (a) => ({ keys: ints(a, 'keys') }),
    apply(tx, p) {
      const wanted = new Set(existingKeys(tx, p.keys));
      const tracks = new Set<TrackId>();
      for (const id of wanted) {
        const k = tx.doc.keys.get(id);
        if (k !== undefined) tracks.add(k.track);
      }
      // Only keys that are still redundant on the current document: re-derived from the tracks' straight runs.
      const remove: KeyId[] = [];
      for (const track of tracks) {
        for (const run of linearRuns(keysOfTrack(tx.doc, tx.idx, track), 2)) {
          for (const id of redundantLinearKeys(run.keys, tx.tempo)) if (wanted.has(id)) remove.push(id);
        }
      }
      if (remove.length === 0) gone(tx);
      Core.keys.deleteKeys(tx, { keys: remove });
    },
  }),

  'problems.fix.addTag': sync({
    parse: (a) => ({ tag: str(a, 'tag').trim() }),
    apply(tx, p) {
      const tags = tx.doc.meta.tags;
      if (tags.some((t) => t.trim().toLowerCase() === p.tag.toLowerCase())) return;
      Core.doc.patchMeta(tx, { patch: { tags: [...tags, p.tag] } });
    },
  }),
};

// ---------------------------------------------------------------------------------------------------------------
// Running
// ---------------------------------------------------------------------------------------------------------------

function transact(env: FixEnv, id: AnyFixCommandId, build: (tx: TxBuilder) => void): FixOutcome {
  const store = env.store;
  if (store === null) return { ok: false, reason: ref(m.fixNoProject) };
  const out = store.transact({ label: env.label ?? ref(TITLES[id]), command: id }, build);
  if (!out.ok) return { ok: false, reason: out.reason };
  return { ok: true, txn: out.txn, notes: out.notes };
}

const MEDIA_FAILURE_MESSAGES: Readonly<Record<MediaFailure, MessageKey>> = {
  read: m.fixMediaRead,
  noDecoder: m.fixMediaNoDecoder,
  decodeAudio: m.fixMediaDecodeAudio,
  noAudio: m.fixMediaNoAudio,
  image: m.fixMediaImage,
  gif: m.fixMediaGif,
  store: m.fixMediaStore,
};

/** The localised reason of a failed media conversion (the error itself goes to the console). */
export function mediaFailureReason(e: unknown): MessageRef {
  return ref(e instanceof MediaFixError ? MEDIA_FAILURE_MESSAGES[e.kind] : m.fixMediaFailed);
}

/** Runs a `problems.fix.*` command body: one transaction, or a failure reason. */
export async function runFix(id: string, args: unknown, env: FixEnv): Promise<FixOutcome> {
  if (!isFixCommand(id)) return { ok: false, reason: ref(m.fixInvalid) };
  const spec = SPECS[id];
  let parsed: unknown;
  try {
    parsed = spec.parse(argsObject(args));
  } catch (e) {
    if (e instanceof ArgError || e instanceof RangeError) return { ok: false, reason: ref(m.fixInvalid) };
    throw e;
  }
  if (spec.kind === 'sync') {
    return transact(env, id, (tx) => {
      spec.apply(tx, parsed, env);
    });
  }
  const store = env.store;
  if (store === null) return { ok: false, reason: ref(m.fixNoProject) };
  if (env.media === null) return { ok: false, reason: ref(m.fixUnavailable) };
  const from = store.doc.assets.get(spec.source(parsed));
  if (from === undefined) return { ok: false, reason: ref(m.fixGone) };
  if (from.missing === true) return { ok: false, reason: ref(m.fixInvalid) };
  let made: AssetRef;
  try {
    made = await spec.produce(parsed, from, env.media);
  } catch (e) {
    console.error(`[checker] ${id} could not convert ${from.path}:`, e);
    return { ok: false, reason: mediaFailureReason(e) };
  }
  return transact(env, id, (tx) => {
    if (!tx.doc.assets.has(from.id)) gone(tx);
    replaceWithNew(tx, from.id, made, made.path);
  });
}

/** Toasts the outcome of a fix: the reason of a failure, or the fix's label with an Undo link. */
export function reportFix(ui: Pick<UiService, 'toast'>, out: FixOutcome, label: MessageRef | null): void {
  if (!out.ok) {
    ui.toast(out.reason, { kind: 'error' });
    return;
  }
  for (const note of out.notes) ui.toast(note, { kind: 'info' });
  if (out.txn === null) ui.toast(ref(m.fixNothingChanged), { kind: 'info' });
  else ui.toast(label ?? ref(m.txnFix), { kind: 'success', undo: true });
}

/** The environment of a fix run as a command (snap and X grid from the editor, media from the services). */
export function fixEnvOf(
  ctx: Pick<CommandContext, 'store' | 'editor' | 'persist' | 'io' | 'dsp'>,
  label: MessageRef | null,
): FixEnv {
  const snap = ctx.editor.getState().snap;
  return {
    store: ctx.store,
    snapDiv: snap.div,
    xGrid: snap.xGrid,
    label,
    media: { persist: ctx.persist, io: ctx.io, dsp: ctx.dsp },
  };
}

export interface FixCommandHooks {
  /** Label of the fix being applied (history entry and toast), null when unknown. */
  pendingLabel(ctx: CommandContext): MessageRef | null;
}

/** The registered `problems.fix.*` commands. */
export function fixCommandDefs(hooks: FixCommandHooks): CommandDef[] {
  return FIX_COMMAND_IDS.map((id): CommandDef => ({
    id,
    title: TITLES[id],
    category: 'Edit',
    icon: ICONS[id] ?? 'Wrench',
    level: 'guided',
    when: FIX_COMMAND_WHEN,
    enabled: (ctx) => (ctx.store === null ? ref(m.reasonNoProject) : true),
    run: async (ctx, args) => {
      const label = hooks.pendingLabel(ctx);
      const out = await runFix(id, args, fixEnvOf(ctx, label));
      reportFix(ctx.ui, out, label);
    },
  }));
}
